// Eskom PARCC — toast notifications.
//
// A context-based toast STACK. `ToastProvider` renders bottom-right, and
// `useToast().show(message, kind?)` pushes an entry that auto-dismisses. The
// signature is unchanged from the single-slot version, so every existing caller
// keeps working — the difference is that two saves in quick succession now both
// get seen instead of the second silently replacing the first.
//
// Both themes render the same stack; Legacy keeps the `.toast` look it has always
// had, Lumen gets the handoff's card with a tone-coloured left border.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { useVariant } from '../../lib/uiPrefs';

export type ToastKind = 'info' | 'success' | 'error';

interface ToastApi {
  show: (message: string, kind?: ToastKind) => void;
}

interface ToastEntry {
  message: string;
  kind: ToastKind;
  id: number;
}

const ToastContext = createContext<ToastApi | null>(null);

const DISMISS_MS = 4000;
/** Beyond this, the oldest is dropped — a stack, not a wall. */
const MAX_VISIBLE = 4;

// Border accents per kind, layered onto the base `.toast`; `info` uses the
// stylesheet's default border.
const KIND_BORDER: Record<ToastKind, string | undefined> = {
  info: undefined,
  success: '1px solid rgba(59,178,115,.55)',
  error: '1px solid rgba(214,69,69,.6)',
};

const KIND_CLASS: Record<ToastKind, string> = {
  info: 'kind-info',
  success: 'kind-success',
  error: 'kind-error',
};

export function ToastProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const counter = useRef(0);
  const variant = useVariant();

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const show = useCallback((message: string, kind: ToastKind = 'info') => {
    counter.current += 1;
    const id = counter.current;
    setToasts((list) => [...list, { message, kind, id }].slice(-MAX_VISIBLE));
    timers.current.set(
      id,
      setTimeout(() => {
        timers.current.delete(id);
        setToasts((list) => list.filter((t) => t.id !== id));
      }, DISMISS_MS),
    );
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {toasts.length > 0 && (
        <div
          className={variant === 'lumen' ? 'lm-toaststack' : undefined}
          style={
            variant === 'lumen'
              ? undefined
              : {
                  position: 'fixed',
                  right: 20,
                  bottom: 20,
                  zIndex: 400,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  pointerEvents: 'none',
                }
          }
        >
          {toasts.map((toast) =>
            variant === 'lumen' ? (
              <div
                key={toast.id}
                className={`lm-toast ${KIND_CLASS[toast.kind]}`}
                role={toast.kind === 'error' ? 'alert' : 'status'}
                aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
              >
                <span className="msg">{toast.message}</span>
                <button
                  type="button"
                  className="dismiss"
                  onClick={() => dismiss(toast.id)}
                  aria-label="Dismiss notification"
                >
                  <X size={13} aria-hidden />
                </button>
              </div>
            ) : (
              <div
                key={toast.id}
                className="toast show"
                role={toast.kind === 'error' ? 'alert' : 'status'}
                aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
                style={{
                  pointerEvents: 'auto',
                  position: 'static',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  ...(KIND_BORDER[toast.kind] ? { border: KIND_BORDER[toast.kind] } : {}),
                }}
              >
                <span style={{ flex: 1 }}>{toast.message}</span>
                <button
                  type="button"
                  className="btn ghost icon-only"
                  onClick={() => dismiss(toast.id)}
                  aria-label="Dismiss notification"
                  style={{ padding: 2, lineHeight: 0 }}
                >
                  <X size={13} aria-hidden />
                </button>
              </div>
            ),
          )}
        </div>
      )}
    </ToastContext.Provider>
  );
}

/** Access the toast API. Throws if used outside a {@link ToastProvider}. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>.');
  }
  return ctx;
}
